import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Alert } from '../components/shared/Alert';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { Lock } from 'lucide-react';
import '../styles/modern.css';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // TODO: Implement remember me functionality (currently unused)
  // const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left Side - Branding (Washi: paper, ink, one vermilion seal) */}
      <div className="hidden lg:flex lg:w-1/2 border-r border-gray-200">
        <div className="flex flex-col justify-between p-12 xl:px-18 w-full">
          {/* Logo & Brand */}
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-7 h-7 bg-red-600 text-white font-serif text-base leading-none">安</span>
            <span className="font-serif text-[17px] tracking-[0.12em] text-gray-900">TSAP</span>
          </div>

          {/* Value Proposition */}
          <div className="flex items-start gap-10">
            <div className="font-serif text-[13px] tracking-[0.5em] text-gray-400 [writing-mode:vertical-rl]" aria-hidden="true">
              安全は静けさから
            </div>
            <div className="max-w-md">
              <h2 className="font-serif text-4xl leading-snug text-gray-900">
                Comprehensive fleet safety management
              </h2>
              <p className="mt-4 text-base leading-relaxed text-gray-600">
                Monitor compliance, track performance, and ensure safety across your entire transportation network.
              </p>

              <div className="mt-10 border-t border-gray-200">
                {[
                  ['Fair safety scores', 'Bias-corrected BASIC percentiles for carriers, recalculated daily'],
                  ['Fraud and chameleon checks', 'Shared-VIN lineage, shell-game and Do-Not-Use audits'],
                  ['Access that fits your role', 'Carrier accounts see only their own DOT; brokers, shippers and insurers see the market']
                ].map(([title, detail]) => (
                  <div key={title} className="py-4 border-b border-gray-200">
                    <p className="text-sm text-gray-900">{title}</p>
                    <p className="text-sm text-gray-500">{detail}</p>
                  </div>
                ))}
              </div>

            </div>
          </div>

          {/* Footer */}
          <div className="text-xs text-gray-400">
            © {new Date().getFullYear()} Transportation Safety Platform. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="flex justify-center items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-7 h-7 bg-red-600 text-white font-serif text-base leading-none">安</span>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--color-gray-900)' }}>
                Transportation Safety
              </h1>
            </div>
          </div>

          {/* Login Card */}
          <div style={{
            background: 'var(--color-gray-50)',
            borderRadius: 'var(--radius-xl)',
            padding: '48px',
            border: '1px solid var(--color-gray-200)'
          }} className="fade-in-up">
            {/* Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-gray-900)' }}>
                Welcome back
              </h2>
              <p className="text-muted">
                Sign in to access your safety dashboard
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6">
                <Alert type="danger" message={error} onClose={() => setError('')} />
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold mb-2"
                  style={{ color: 'var(--color-gray-700)' }}
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '15px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-lg)',
                    transition: 'all 0.15s ease',
                    color: 'var(--color-gray-900)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(35, 33, 29, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-gray-300)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Password Input */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold mb-2"
                  style={{ color: 'var(--color-gray-700)' }}
                >
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '15px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-lg)',
                    transition: 'all 0.15s ease',
                    color: 'var(--color-gray-900)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(35, 33, 29, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-gray-300)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                {/* TODO: Implement remember me functionality - placeholder for future
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer',
                      accentColor: 'var(--color-primary)'
                    }}
                  />
                  <span className="text-sm" style={{ color: 'var(--color-gray-600)' }}>
                    Remember me
                  </span>
                </label>
                */}
                <div />
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium"
                  style={{ color: 'var(--color-primary)' }}
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
                style={{
                  width: '100%',
                  height: '48px',
                  fontSize: '15px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? (
                  <>
                    <LoadingSpinner message="Signing in..." size="sm" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    <span>Sign in securely</span>
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: '1px',
                  background: 'var(--color-gray-200)'
                }} />
                <div className="relative text-center">
                  <span style={{
                    background: '#fbf9f4',
                    padding: '0 12px',
                    fontSize: '13px',
                    color: 'var(--color-gray-500)'
                  }}>
                    New to the platform?
                  </span>
                </div>
              </div>

              {/* Sign Up Link */}
              <div className="text-center">
                <Link
                  to="/register"
                  style={{
                    display: 'inline-block',
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--color-primary)',
                    textDecoration: 'none',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-lg)',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--color-gray-50)';
                    e.currentTarget.style.borderColor = 'var(--color-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'var(--color-gray-300)';
                  }}
                >
                  Create an account
                </Link>
              </div>
            </form>

            {/* Test Account Info */}
          </div>

          {/* Footer Links */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-6 text-sm" style={{ color: 'var(--color-gray-500)' }}>
              <Link to="/privacy" style={{ color: 'var(--color-gray-600)' }}>
                Privacy Policy
              </Link>
              <span>•</span>
              <Link to="/terms" style={{ color: 'var(--color-gray-600)' }}>
                Terms of Service
              </Link>
              <span>•</span>
              <Link to="/contact" style={{ color: 'var(--color-gray-600)' }}>
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
